export function generateMetricDay(minCount = 50, maxCount = 500, matric1= "Current Year", matric2 = "Last Year", baseDate = "2025-10-11") {
  // Helper to format hours into ISO UTC time
  const makeNode = (hour) => {
    const date = new Date(`${baseDate}T${String(hour).padStart(2, "0")}:00:00Z`);
    return date.toISOString(); // ensures UTC format
  };

  // Generate 6 nodes with 4-hour gaps
  const hours = [0, 4, 8, 12, 16, 20];

  const currentYearAnalytics = hours.map((h) => ({
    node: makeNode(h),
    count: Math.floor(Math.random() * maxCount) + minCount, // random 50–550
  }));

  const lastYearAnalytics = hours.map((h) => ({
    node: makeNode(h),
    count: Math.floor(Math.random() * maxCount) + minCount, // random 50–550
  }));

  return [
    {
      metricName: matric1,
      analytics: currentYearAnalytics,
    },
    {
      metricName: matric2,
      analytics: lastYearAnalytics,
    },
  ];
}

export function generateMetricWeek(minCount = 50, maxCount = 80, matric1= "Current Year", matric2 = "Last Year", startDate = "2025-10-11", days = 7) {
  const metrics = [matric1, matric2];

  return metrics.map((metricName, metricIndex) => {
    const analytics = [];

    for (let i = 0; i < days; i++) {
      // Start date shifted by i days
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);

      // Add some random hours/minutes for variation
      date.setHours(5 + i, (i * 15) % 60, 0, 0);

      // Generate smooth counts: base + variation
      const base = metricIndex === 0 ? 200 : 100; // Current year generally higher
      const count = Math.floor(base + (i * minCount) + Math.random() * maxCount);

      analytics.push({
        node: date.toISOString(),
        count,
      });
    }

    return {
      metricName,
      analytics,
    };
  });
}

export function generateMetricMonth(minCount = 50, maxCount = 500, matric1= "Current Year", matric2 = "Last Year") {
  const analyticsData = [];
  const currentYear = {
    metricName: matric1,
    analytics: []
  };
  const lastYear = {
    metricName: matric2,
    analytics: []
  };

  // Helper: generate smooth random values around a base
  function generateSmoothValue(base, variation, min = minCount, max = maxCount) {
    let value = base + Math.floor((Math.random() - 0.5) * variation);
    return Math.max(min, Math.min(max, value)); // clamp to min-max
  }

  // Start with base counts (somewhere mid-range)
  let currentBase = 250;
  let lastYearBase = 200;

  for (let day = 1; day <= 30; day++) {
    const dateStr = `2025-10-${String(day).padStart(2, "0")}T12:00:00`;

    // Smooth variation (+/-50 around base)
    currentBase = generateSmoothValue(currentBase, 60);
    lastYearBase = generateSmoothValue(lastYearBase, 60);

    currentYear.analytics.push({
      node: dateStr,
      count: currentBase
    });

    lastYear.analytics.push({
      node: dateStr,
      count: lastYearBase
    });
  }

  analyticsData.push(currentYear, lastYear);
  return analyticsData;
}

export function generateMetricYear(minCount = 50, maxCount = 1000, matric1= "Current Year", matric2 = "Last Year") {
  const analyticsData = [];
  const baseYear = new Date().getFullYear();

  // Helper function to create monthly data
  function generateYearData(yearOffset = 0, min = minCount, max = maxCount) {
    const data = [];
    let count = Math.floor(Math.random() * (max - min) + min);

    for (let month = 1; month <= 12; month++) {
      // Ensure smooth progression with slight random variation
      count += Math.floor(Math.random() * 100) - 30; // small variation
      if (count < min) count = min;
      if (count > max) count = max;

      data.push({
        node: `${baseYear - yearOffset}-${String(month).padStart(2, "0")}-11T${11 + month % 5}:00:00`,
        count: parseFloat(count.toFixed(1)),
      });
    }
    return data;
  }

  analyticsData.push({
    metricName: matric1,
    analytics: generateYearData(0, 50, 1000),
  });

  analyticsData.push({
    metricName: matric2,
    analytics: generateYearData(0, 80, 1000),
  });

  return analyticsData;
}
// =====================================================================================

export function generateDayData(minCount = 5, maxCount = 15) {
  const date = "2025-10-11";
  const analyticsData = [];
  const startHour = 3; // start time 03:00:00
  const interval = 4;  // 4 hour gap
  const intervalsPerDay = 4; // total 4 data points

  for (let i = 0; i < intervalsPerDay; i++) {
    const hour = startHour + i * interval;
    const node = new Date(`${date}T${String(hour).padStart(2, "0")}:00:00Z`);

    analyticsData.push({
      node: node.toISOString(),
      count: Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount,
    });
  }

  return analyticsData;
}

export function generateWeekData(minCount = 5, maxCount = 15) {
  const startDate = "2025-10-09";
  const days = 7;
  const analyticsData = [];
  const start = new Date(startDate);

  for (let i = 0; i < days; i++) {
    const current = new Date(start);
    current.setDate(start.getDate() + i);

    analyticsData.push({
      node: current.toISOString(),
      count: Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount,
    });
  }

  return analyticsData;
}


export function generateMonthData(
  minCount = 50,
  maxCount = 500,
  year = 2025,
  month = 10 // month in 1–12
) {
  // Get total days in given month
  const daysInMonth = new Date(year, month, 0).getDate();

  const analyticsData = [];

  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month - 1, i); // JS months are 0-indexed
    analyticsData.push({
      node: date.toISOString(), // ISO string for each date
      count: Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount,
    });
  }

  return analyticsData;
}

export function generateYearData(minCount = 5, maxCount = 15, year = "2025") {
  const analyticsData = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(Date.UTC(year, i, 1)); // i = month (0-11)
    return {
      node: date.toISOString(), // ISO format
      count: Math.floor(Math.random() * (maxCount - minCount + 1)) + minCount,
    };
  });

  return analyticsData;
}